import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

// SETUP COLORS
// Warm-toned grey scale that complements the red/yellow brand
const GREY = {
  0: '#FFFFFF',
  100: '#F1F5F9',
  200: '#E2E8F0',
  300: '#CBD5E1',
  400: '#94A3B8',
  500: '#64748B',
  600: '#475569',
  700: '#334155',
  800: '#1E293B',
  900: '#0F172A',
  500_8: alpha('#64748B', 0.08),
  500_12: alpha('#64748B', 0.12),
  500_16: alpha('#64748B', 0.16),
  500_24: alpha('#64748B', 0.24),
  500_32: alpha('#64748B', 0.32),
  500_48: alpha('#64748B', 0.48),
  500_56: alpha('#64748B', 0.56),
  500_80: alpha('#64748B', 0.8),
};

const PRIMARY = {
  lighter: '#FBD1DA',
  light: '#F47A95',
  main: '#ED254E',
  dark: '#C41A3D',
  darker: '#7A0C2E',
  contrastText: '#fff',
};

const SECONDARY = {
  lighter: '#FEF5D0',
  light: '#FBE88A',
  main: '#F9DC5C',
  dark: '#D4B830',
  darker: '#8A7810',
  contrastText: '#212B36',
};

const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
  contrastText: '#fff',
};

const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
  contrastText: GREY[800],
};

const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
  contrastText: '#fff',
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

const shared = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY },
  secondary: { ...SECONDARY },
  info: { ...INFO },
  success: { ...SUCCESS },
  warning: { ...WARNING },
  error: { ...ERROR },
  grey: GREY,
  chart: CHART_COLORS,
};

export default function getPalette(mode = 'light') {
  if (mode === 'dark') {
    return {
      ...shared,
      mode: 'dark',
      divider: GREY[500_24],
      // disabled sits between GREY[400] and GREY[500]: GREY[500] (#64748B) is
      // 3.9:1 on the #0B0E14 default background — below AA for the many
      // informative uses of text.disabled (timestamps, stat labels, helper
      // copy). #7B8AA0 clears 4.5:1 while staying dimmer than secondary.
      text: { primary: '#FFFFFF', secondary: '#94A3B8', disabled: '#7B8AA0' },
      background: { paper: '#151A24', default: '#0B0E14', neutral: '#1E2533', dark: '#000000' },
      action: {
        active: GREY[400],
        hover: GREY[500_8],
        selected: GREY[500_16],
        disabled: GREY[500_80],
        disabledBackground: GREY[500_24],
        focus: GREY[500_24],
        hoverOpacity: 0.08,
        disabledOpacity: 0.48,
      },
    };
  }

  return {
    ...shared,
    mode: 'light',
    divider: GREY[500_24],
    text: { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] },
    background: { paper: '#FFFFFF', default: '#F8FAFC', neutral: '#F1F5F9', dark: '#0F172A' },
    action: {
      active: GREY[600],
      hover: GREY[500_8],
      selected: GREY[500_16],
      disabled: GREY[500_80],
      disabledBackground: GREY[500_24],
      focus: GREY[500_24],
      hoverOpacity: 0.08,
      disabledOpacity: 0.48,
    },
  };
}
